/**
 * 图片 URL 转换工具
 * 解决外网访问时，内网图片地址（如 192.168.x.x）导致的跨域问题
 * 
 * 方案：将内网 MinIO/PicList 地址转换为 nginx 代理的相对路径
 * 原始地址：http://192.168.1.2:6001/bucket-name/path/to/image.jpg
 * 转换后：  /storage/bucket-name/path/to/image.jpg
 * 
 * 环境适配：
 * - 内网访问（192.168.x.x 或 localhost）：直接使用原始 URL，因为可以直接访问
 * - 外网访问（其他地址）：转换为 /storage/ 代理路径
 */

// 内网 MinIO 服务地址模式（支持多个内网地址配置）
const INTERNAL_STORAGE_PATTERNS = [
  /^https?:\/\/192\.168\.\d+\.\d+(:\d+)?\//,  // 192.168.x.x
  /^https?:\/\/10\.\d+\.\d+\.\d+(:\d+)?\//,    // 10.x.x.x
  /^https?:\/\/172\.(1[6-9]|2\d|3[01])\.\d+\.\d+(:\d+)?\//,  // 172.16-31.x.x
  /^https?:\/\/localhost(:\d+)?\//,             // localhost
  /^https?:\/\/127\.0\.0\.1(:\d+)?\//,          // 127.0.0.1
  /^https?:\/\/oss\.sumarts\.uk(:\d+)?\//,      // Cloudflare OSS 域名
  /^https?:\/\/sumarts\.uk(:\d+)?\//,           // 主域名
];

// 代理路径前缀
const STORAGE_PROXY_PREFIX = '/storage/';

/**
 * 检查 URL 是否为内网存储地址
 * @param {string} url - 原始图片 URL
 * @returns {boolean}
 */
export const isInternalStorageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return INTERNAL_STORAGE_PATTERNS.some(pattern => pattern.test(url));
};

/**
 * 将内网存储 URL 转换为代理 URL
 * @param {string} url - 原始图片 URL
 * @returns {string} - 转换后的 URL
 */
export const convertToProxyUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  // 如果已经是相对路径或代理路径，直接返回
  if (url.startsWith('/') || url.startsWith('data:')) {
    return url;
  }
  
  // 外网访问时，检查是否为内网地址或外部 OSS 地址，统一转换为代理路径
  let shouldConvert = false;
  for (const pattern of INTERNAL_STORAGE_PATTERNS) {
    if (pattern.test(url)) {
      shouldConvert = true;
      break;
    }
  }

  // 特殊兜底：只要包含核心桶名 piclist-sumart，也尝试走代理（防止一些奇怪的 IP/域名没匹配上）
  if (!shouldConvert && url.includes('/piclist-sumart/')) {
    shouldConvert = true;
  }

  if (shouldConvert) {
    try {
      const urlObj = new URL(url);
      // 返回 /storage + 原始路径 + 查询参数（保留签名）
      return STORAGE_PROXY_PREFIX.slice(0, -1) + urlObj.pathname + urlObj.search;
    } catch (e) {
      console.warn('Invalid URL:', url, e);
      return url;
    }
  }
  
  // 完全无关的 URL，保持原样直连
  return url;
};

/**
 * 批量转换图片 URL 数组
 * @param {string[]} urls - 原始图片 URL 数组
 * @returns {string[]} - 转换后的 URL 数组
 */
export const convertUrlsToProxy = (urls) => {
  if (!Array.isArray(urls)) return urls;
  return urls.map(convertToProxyUrl);
};

/**
 * 创建安全的图片 src 属性值
 * 自动处理内网地址转换
 * @param {string} url - 原始图片 URL
 * @returns {string}
 */
export const safeImageSrc = (url) => {
  return convertToProxyUrl(url) || '';
};

export default {
  isInternalStorageUrl,
  convertToProxyUrl,
  convertUrlsToProxy,
  safeImageSrc,
};
